"use client";

import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { X, CreditCard, Check, Loader2, ShieldCheck } from "lucide-react";
import { trackCardSetupInitiated } from "@/lib/analytics";
import { CARD_ON_FILE_BONUS_CREDITS } from "@/lib/plans";
import {
  cardCopyFor,
  coerceCardCopyVariant,
} from "@/lib/billing/card-copy-experiment";
import { redirectToBilling } from "@/lib/intentional-navigation";
import { interceptIfPaymentsPaused } from "@/components/billing/payments-paused-store";
import { useResetOnBfcache } from "@/lib/use-reset-on-bfcache";

interface CardEntryModalProps {
  open: boolean;
  onClose: () => void;
  projectId?: string;
  /**
   * Internal path to send the user back to after Stripe finishes (e.g.
   * "/profile"). When omitted, setup-card falls back to /project/:id or
   * /projects so callers from those contexts don't need to opt in.
   */
  returnTo?: string;
  /** Optional: lets the parent refine the "why you're seeing this" copy. */
  creditsRemaining?: number;
  /** Block type that triggered this modal (e.g. "project-create"). Used by
   * the project page after Stripe redirects back to decide whether to
   * auto-resume the user's blocked prompt. */
  blockedAction?: string;
  /** Prompt the user typed before being blocked. Stashed in localStorage so
   * /project/[id] can auto-run it after the card-on-file credits land. */
  blockedPrompt?: string;
  /** Template chosen alongside the blocked prompt. */
  blockedTemplateId?: string;
  /**
   * Card-copy A/B arm (GT-15), resolved server-side and delivered by
   * GET /api/credits as `card_copy_variant`. Anything unrecognised — a stale
   * client, a failed fetch, a caller that doesn't pass it — degrades to
   * "control", so nobody enters the test arm by accident.
   *
   * Scoped to `mode: "add"`. The experiment asks what persuades someone to
   * save a FIRST card; an update is a different errand, usually arrived at
   * from a failed charge, and its copy is not under test.
   */
  copyVariant?: string | null;
  /**
   * "add" (default) — the first-card unlock: no card on file, +bonus credits.
   * "update" — REPLACE the card already on file.
   *
   * These are different requests, not different copy over one request.
   * /api/stripe/setup-card refuses an "add" outright when a payment method
   * already exists ("Card already on file", 409) and only the "update" intent
   * is exempt — it is also what makes the webhook promote the new card to the
   * default on the customer AND on every active subscription, which is the
   * whole point when the reason for being here is a failed charge.
   *
   * Without this the modal could only ever add a FIRST card, so the billing
   * page's own "Update" button — which renders exactly when a card IS on file —
   * always 409'd. The payment-issue banner then walked users straight into it.
   */
  mode?: "add" | "update";
}

export function CardEntryModal({
  open,
  onClose,
  projectId,
  returnTo,
  blockedAction,
  blockedPrompt,
  blockedTemplateId,
  copyVariant,
  mode = "add",
}: CardEntryModalProps) {
  const updating = mode === "update";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const variant = coerceCardCopyVariant(copyVariant);
  // Both arms' strings live in the experiment module so they can't drift, and
  // every quoted amount comes from CARD_ON_FILE_BONUS_CREDITS — the same
  // single-source rule lib/plans.ts documents (PANDA-1349/1359).
  const copy = cardCopyFor(variant, CARD_ON_FILE_BONUS_CREDITS);

  // Browser back from the hosted card-setup page restores this modal from the
  // back-forward cache with `loading` still true, leaving the button stuck on
  // "Redirecting to checkout…" (PANDA-1461). Clear it on a bfcache restore.
  useResetOnBfcache(useCallback(() => setLoading(false), []));

  if (!open) return null;

  async function handleAddCard() {
    // Card setup is a Stripe SetupIntent session and fails with the rest. No
    // label/price — nothing is being purchased at this step.
    if (
      interceptIfPaymentsPaused({
        kind: "upgrade",
        surface: "card_entry_modal",
      })
    ) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Stash the blocked prompt + templateId so /project/[id] can
      // auto-resume the build after Stripe redirects back with
      // ?card_added=true. Mirrors the upgrade-modal pattern; project
      // page only acts on the stash for INIT_BUILD_BLOCKS actions.
      if (projectId) {
        const trimmedPrompt =
          typeof blockedPrompt === "string" ? blockedPrompt.trim() : "";
        const payload = {
          projectId,
          blockedAction,
          timestamp: Date.now(),
          ...(trimmedPrompt ? { prompt: trimmedPrompt } : {}),
          ...(blockedTemplateId ? { templateId: blockedTemplateId } : {}),
        };
        console.log("[CARD-DEBUG][card-modal] stashing panda_upgrade_resume", payload);
        localStorage.setItem("panda_upgrade_resume", JSON.stringify(payload));
      } else {
        console.log("[CARD-DEBUG][card-modal] no projectId — skipping stash");
      }
      const res = await fetch("/api/stripe/setup-card", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          returnTo,
          ...(updating ? { intent: "update" } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        setError(data.error || "Failed to start card setup");
        setLoading(false);
        return;
      }
      // INTENT: card-setup URL received, about to redirect to Stripe.
      trackCardSetupInitiated(
        updating ? "update" : "add",
        "card_entry_modal",
        projectId,
      );
      redirectToBilling(data.url);
    } catch {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  return createPortal(
    // Overlay scrolls so the card is fully reachable on short/landscape
    // screens instead of being clipped; min-h-full + items-center keeps it
    // centred when it fits, and the wrapper padding honours the iOS
    // safe-area insets (PANDA-1565).
    <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-black/50 backdrop-blur-sm">
      <div className="flex min-h-full items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))]">
        <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700 dark:bg-zinc-900">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="border-b border-zinc-100 bg-gradient-to-r from-emerald-50 to-sky-50 px-6 py-6 dark:border-zinc-800 dark:from-emerald-950/20 dark:to-sky-950/20">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500 text-white">
              <CreditCard className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                {updating ? "Update your payment method" : copy.title}
              </h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                {updating
                  ? "Your new card replaces the one on file and is used for your subscription."
                  : copy.subtitle}
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          {/* An update earns no bonus and makes no "you won't be charged"
              promise: the reason someone is replacing a card is usually an
              outstanding invoice, which the processor retries as soon as a
              usable card is attached. Saying otherwise here would be the lie
              they discover on their statement.

              Copy stays provider-NEUTRAL because this modal serves both
              processors: /api/stripe/setup-card branches on isPaddle(), so the
              same bullets render over a Paddle checkout whenever
              NEXT_PUBLIC_PAYMENT_PROVIDER=paddle. Naming one processor here is
              wrong half the time, and the flag is designed to be flipped back
              (lib/payments/provider.ts). */}
          <ul className="space-y-3 text-sm text-zinc-700 dark:text-zinc-300">
            {updating ? (
              <>
                <li className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  <span>
                    Becomes the default card for your subscription straight away
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  <span>
                    If a payment is outstanding, it is retried on the new card
                  </span>
                </li>
              </>
            ) : (
              <>
                <li className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  <span>
                    <strong>{copy.bonusLine}</strong> added to your account
                    instantly
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  <span>{copy.noChargeLine}</span>
                </li>
              </>
            )}
            <li className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
              <span>Securely processed and stored by our payment provider</span>
            </li>
          </ul>

          {error && (
            <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-400">
              {error}
            </p>
          )}

          <button
            onClick={handleAddCard}
            disabled={loading}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Redirecting to checkout…
              </>
            ) : updating ? (
              <>Continue to secure checkout</>
            ) : (
              <>Add card and unlock {CARD_ON_FILE_BONUS_CREDITS} credits</>
            )}
          </button>

          <button
            onClick={onClose}
            className="mt-2 w-full rounded-full px-4 py-2 text-sm text-zinc-500 transition-colors hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
          >
            Not now
          </button>
        </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
